// File: frontend/src/pages/KnowledgeGraph/components/EntitySearchBar.tsx
import React from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Paper,
  Typography,
  Chip,
  Skeleton,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  KGSearchResult,
  ENTITY_TYPE_OPTIONS,
  ENTITY_TYPE_COLORS,
} from '../types';
import { cyber } from '../../../theme';

interface EntitySearchBarProps {
  query: string;
  onQueryChange: (q: string) => void;
  entityTypeFilter: string;
  onEntityTypeFilterChange: (t: string) => void;
  onSearch: () => void;
  results: KGSearchResult[];
  loading: boolean;
  onSelectEntity: (entity: KGSearchResult) => void;
}

const EntitySearchBar: React.FC<EntitySearchBarProps> = ({
  query,
  onQueryChange,
  entityTypeFilter,
  onEntityTypeFilterChange,
  onSearch,
  results,
  loading,
  onSelectEntity,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onSearch();
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search entities... (e.g., APT28, Cobalt Strike, T1566)"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={handleKeyDown}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            },
          }}
          size="small"
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Entity Type</InputLabel>
          <Select
            value={entityTypeFilter}
            label="Entity Type"
            onChange={(e) => onEntityTypeFilterChange(e.target.value)}
          >
            {ENTITY_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Search results */}
      {(loading || results.length > 0) && (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            overflowX: 'auto',
            pb: 1,
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
          }}
        >
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" width={220} height={80} sx={{ flexShrink: 0 }} />
              ))
            : results.map((entity) => (
                <Paper
                  key={entity.id}
                  onClick={() => onSelectEntity(entity)}
                  sx={{
                    p: 1.5,
                    minWidth: 220,
                    maxWidth: 280,
                    flexShrink: 0,
                    cursor: 'pointer',
                    border: `1px solid ${cyber.border}`,
                    boxShadow: 'none',
                    '&:hover': { borderColor: 'primary.main' },
                    transition: 'border-color 0.2s',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ fontFamily: cyber.fontMono }}>
                      {entity.name}
                    </Typography>
                    <Chip
                      label={entity.type}
                      size="small"
                      sx={{
                        bgcolor: ENTITY_TYPE_COLORS[entity.type] || '#6b7280',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        height: 20,
                      }}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    <Box component="span" sx={{ fontFamily: cyber.fontMono, color: cyber.accent.cyan }}>
                      {entity.report_count}
                    </Box>{' '}
                    reports
                  </Typography>
                  {entity.aliases && entity.aliases.length > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      aka: {entity.aliases.slice(0, 3).join(', ')}
                    </Typography>
                  )}
                </Paper>
              ))}
        </Stack>
      )}
    </Box>
  );
};

export default EntitySearchBar;
